import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Plus, Copy, Trash2, X, Dumbbell, Eye, Target, ShieldAlert, Layers, Wrench } from "lucide-react";
import { PageHead } from "@/components/AppLayout";
import { Card, Btn, EmptyState, SearchInput } from "@/components/ui-kit";
import api from "@/lib/api";

const CATS = ["All", "Knee & Hip", "Shoulder & Elbow", "Spine", "Neuro & Cardiac"];
const CAT_COLOR = { "Knee & Hip": "#1A3A6D", "Shoulder & Elbow": "#4F46E5", "Spine": "#DAA520", "Neuro & Cardiac": "#E05A47" };

export default function Protocols() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: "", category: "Knee & Hip", sessions: 10, weeks: 6, description: "" });

  const load = useCallback(() => api.get("/protocols").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const shown = items.filter((p) => (cat === "All" || p.category === cat) && (!q || p.name.toLowerCase().includes(q.toLowerCase())));

  const create = async () => {
    if (!form.name.trim()) return toast.error("Enter a protocol name");
    await api.post("/protocols", { ...form, sessions: Number(form.sessions), weeks: Number(form.weeks) });
    toast.success("Protocol created"); setModal(false);
    setForm({ name: "", category: "Knee & Hip", sessions: 10, weeks: 6, description: "" }); load();
  };
  const dup = async (p) => { await api.post(`/protocols/${p.id}/duplicate`); toast.success(`Duplicated ${p.name}`); load(); };
  const del = async (p) => { await api.delete(`/protocols/${p.id}`); toast.success("Protocol deleted"); load(); };

  return (
    <>
      <PageHead title="Protocols" subtitle="Your library of clinical rehabilitation protocols."
        action={<Btn variant="primary" data-testid="new-protocol-button" onClick={() => setModal(true)}><Plus size={16} /> New Protocol</Btn>} />

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)} className={`pq-chip ${cat === c ? "pq-chip-active" : ""}`} data-testid={`protocol-cat-${c.replace(/[\s&]/g, "-")}`}>{c}</button>
          ))}
        </div>
        <SearchInput testid="protocol-search" value={q} onChange={setQ} placeholder="Search protocols…" />
      </div>

      {shown.length === 0 ? (
        <Card><EmptyState icon={<BookOpen size={24} />} title="No protocols yet"
          subtitle="Build a reusable rehabilitation protocol to speed up future treatment orders."
          action={<Btn variant="primary" onClick={() => setModal(true)}><Plus size={16} /> New Protocol</Btn>} /></Card>
      ) : (
        <div className="space-y-3">
          {shown.map((p) => (
            <Card key={p.id} hover className="p-4 flex items-center gap-4" data-testid={`protocol-card-${p.id}`}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: CAT_COLOR[p.category] || "#1A3A6D" }}><Dumbbell size={19} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-[14px]">{p.name}</div>
                <div className="text-[12px] text-slate-500" data-testid={`protocol-usage-${p.id}`}>{p.category} · 📅 {p.sessions} Sessions · 🕐 {p.weeks} Weeks · Used in {p.uses || 0} orders</div>
              </div>
              <Btn size="sm" data-testid={`protocol-view-${p.id}`} onClick={() => setDetail(p)}><Eye size={13} /> View</Btn>
              <Btn size="sm" onClick={() => dup(p)}><Copy size={13} /> Duplicate</Btn>
              <Btn size="sm" variant="danger" data-testid={`protocol-delete-${p.id}`} onClick={() => del(p)}><Trash2 size={13} /></Btn>
            </Card>
          ))}
        </div>
      )}

      {modal && (
        <Modal title="Create New Protocol" onClose={() => setModal(false)}>
          <L label="Protocol Name"><input className="pq-input" data-testid="protocol-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Rotator Cuff Rehab" /></L>
          <L label="Category"><select className="pq-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{CATS.slice(1).map((c) => <option key={c}>{c}</option>)}</select></L>
          <div className="grid grid-cols-2 gap-3">
            <L label="Sessions"><input type="number" className="pq-input" value={form.sessions} onChange={(e) => setForm({ ...form, sessions: e.target.value })} /></L>
            <L label="Duration (weeks)"><input type="number" className="pq-input" value={form.weeks} onChange={(e) => setForm({ ...form, weeks: e.target.value })} /></L>
          </div>
          <L label="Description"><textarea className="pq-input min-h-[80px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the protocol's goals and structure..." /></L>
          <div className="flex justify-end gap-2 mt-4"><Btn onClick={() => setModal(false)}>Cancel</Btn><Btn variant="primary" data-testid="create-protocol-submit" onClick={create}>Create Protocol</Btn></div>
        </Modal>
      )}
      {detail && <ProtocolDetailModal p={detail} onClose={() => setDetail(null)} />}
    </>
  );
}

function ProtocolDetailModal({ p, onClose }) {
  const Bullets = ({ items }) => <ul className="list-disc pl-5 space-y-1 text-[12.5px] text-slate-600">{items.map((x) => <li key={x}>{x}</li>)}</ul>;
  const Sec = ({ icon, title, children }) => <div className="mb-4"><div className="pq-label mb-1.5 flex items-center gap-1.5">{icon}{title}</div>{children}</div>;
  return (
    <Modal title={p.name} onClose={onClose}>
      <div className="flex gap-2 flex-wrap mb-4">
        <span className="pq-chip">{p.category}</span>
        <span className="pq-chip">📅 {p.sessions} sessions</span>
        <span className="pq-chip">🕐 {p.weeks} weeks</span>
      </div>
      {p.overview && <Sec icon={<BookOpen size={13} className="text-teal-600" />} title="Overview"><p className="text-[13px] text-slate-600 leading-relaxed">{p.overview}</p></Sec>}
      {p.goals?.length > 0 && <Sec icon={<Target size={13} className="text-teal-600" />} title="Goals"><Bullets items={p.goals} /></Sec>}
      {p.phases?.length > 0 && (
        <Sec icon={<Layers size={13} className="text-teal-600" />} title="Phased Progression">
          {p.phases.map((ph, i) => (
            <div key={ph.title || `phase-${i}`} className="mb-2.5 bg-slate-50 rounded-xl p-3" data-testid={`protocol-phase-${i}`}>
              <div className="font-semibold text-[13px]">{ph.title} {ph.weeks && <span className="text-slate-400 font-normal">· {ph.weeks}</span>}</div>
              {ph.focus && <div className="text-[12px] text-slate-500 mb-1.5">{ph.focus}</div>}
              {ph.exercises?.length > 0 && <ul className="list-disc pl-5 space-y-0.5 text-[12.5px] text-slate-600">{ph.exercises.map((e) => <li key={e}>{e}</li>)}</ul>}
            </div>
          ))}
        </Sec>
      )}
      {p.precautions?.length > 0 && <Sec icon={<ShieldAlert size={13} className="text-amber-600" />} title="Precautions & Contraindications"><Bullets items={p.precautions} /></Sec>}
      {p.equipment?.length > 0 && <Sec icon={<Wrench size={13} className="text-teal-600" />} title="Equipment"><Bullets items={p.equipment} /></Sec>}
      {!p.overview && !p.phases?.length && <p className="text-[13px] text-slate-600">{p.description || "No detailed content for this protocol."}</p>}
      <div className="flex justify-end mt-4"><Btn variant="primary" onClick={onClose}>Close</Btn></div>
    </Modal>
  );
}

export function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-start justify-center p-6 overflow-y-auto animate-fade-in" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-full max-w-lg mt-[6vh] animate-fade-up" style={{ boxShadow: "0 30px 80px rgba(15,23,42,.3)" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200"><h3 className="font-display font-bold text-base">{title}</h3><button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
export function L({ label, children }) {
  return <label className="block mb-3"><span className="block text-[12.5px] font-semibold text-slate-700 mb-1.5">{label}</span>{children}</label>;
}
